/* Copyright (c) 2020-present CloudIO Inc */

import { lighten, darken, Theme, Palette } from '@mui/material';
import { makeStyles } from '@mui/styles';
import styleFunctionSx from '@mui/system/styleFunctionSx';
import type { ThemeColor } from 'componentregistry';
import { backgroundColor as bgColor } from 'componentregistry/registry/constants';
import tinyColor from 'tinycolor2';

export const getColor = (
  theme: Theme,
  color?: ThemeColor | 'inherit' | string | null,
) => {
  if (!color?.length) {
    return theme.palette.text.primary;
  }
  switch (color) {
    case 'ioGrey':
      return theme.ioGrey.contrastText;
    case 'paper':
      return theme.palette.text.primary;
    case 'default':
      return theme.palette.text.primary;
    case 'inherit':
      return 'inherit';
    default:
      if (
        !theme.palette[
          color as Exclude<
            ThemeColor,
            'inherit' | 'default' | 'paper' | 'ioGrey'
          >
        ]
      ) {
        return color;
      }
      return theme.palette[
        color as Exclude<ThemeColor, 'inherit' | 'default' | 'paper' | 'ioGrey'>
      ].contrastText;
  }
};

export const getTextColor = (
  theme: Theme,
  color?: ThemeColor | 'inherit' | string | null,
  hover?: boolean,
) => {
  if (!color?.length) {
    return theme.palette.text.primary;
  }
  switch (color) {
    case 'ioGrey':
      return hover ? theme.ioGrey.main : theme.ioGrey.dark;
    case 'paper':
    case 'text.primary':
      return hover ? theme.palette.primary.main : theme.palette.text.primary;
    case 'text.secondary':
    case 'default':
      return hover ? theme.palette.primary.main : theme.palette.text.secondary;
    case 'divider':
      return hover ? theme.palette.primary.main : theme.palette.divider;
    case 'disabled':
      return theme.palette.action.disabled;
    case 'inherit':
      return 'inherit';
    default:
      if (color.indexOf('.') !== -1) {
        const [key, val] = color.split('.');
        const palette =
          theme.palette[
            key as Exclude<
              ThemeColor,
              'inherit' | 'default' | 'paper' | 'ioGrey'
            >
          ];

        if (
          palette &&
          (val === 'main' ||
            val === 'dark' ||
            val === 'light' ||
            val === 'contrastText')
        ) {
          return palette[val];
        }
      }
      if (
        !theme.palette[
          color as Exclude<
            ThemeColor,
            'inherit' | 'default' | 'paper' | 'ioGrey'
          >
        ]
      ) {
        return color;
      }
      return hover
        ? theme.palette[
            color as Exclude<
              ThemeColor,
              'inherit' | 'default' | 'paper' | 'ioGrey'
            >
          ].dark
        : theme.palette[
            color as Exclude<
              ThemeColor,
              'inherit' | 'default' | 'paper' | 'ioGrey'
            >
          ].main;
  }
};

export const getBGColor = (theme: Theme, color?: string, hover?: boolean) => {
  if (!color?.length) {
    return theme.palette.text.secondary;
  }
  switch (color) {
    case 'ioGrey':
      return hover ? theme.ioGrey.dark : theme.ioGrey.main;
    case 'paper':
      return hover
        ? theme.palette.background.default
        : theme.palette.background.paper;
    case 'default':
      return hover
        ? darken(theme.palette.background.default, 0.1)
        : theme.palette.background.default;
    case 'inherit':
      return 'inherit';
    default:
      if (!theme.palette[color as keyof Palette]) {
        const paletteKeyAndValue = color?.split('.');
        if (
          paletteKeyAndValue.length === 2 &&
          theme.palette[paletteKeyAndValue[0] as keyof Palette] &&
          theme.palette[paletteKeyAndValue[0] as keyof Palette][
            paletteKeyAndValue[1] as keyof Palette[keyof Palette]
          ]
        ) {
          color = paletteKeyAndValue.reduce(
            (acc: any, key) => acc?.[key],
            theme.palette,
          );
        }
        return color;
      }
      // @ts-ignore - color is a key of palette
      return hover ? theme.palette[color].dark : theme.palette[color].main;
  }
};

export const getBorderColor = (theme: Theme, color?: ThemeColor) => {
  if (!color?.length) {
    return theme.palette.divider;
  }
  switch (color) {
    case 'ioGrey':
      return theme.ioGrey.dark;
    case 'paper':
      return theme.palette.background.default;
    case 'default':
    case 'inherit':
      return darken(theme.palette.background.default, 0.1);
    default:
      if (!theme.palette[color]) {
        console.error(`Invalid color ${color} passed to getBGColor()!`);
        return theme.palette.primary.dark;
      }
      return theme.palette[color].dark;
  }
};

export const getLiteColor = (theme: Theme, color?: ThemeColor) => {
  if (!color?.length) {
    return lighten(theme.palette.background.default, 0.95);
  }
  switch (color) {
    case 'ioGrey':
      return lighten(theme.ioGrey.main, 0.95);
    case 'paper':
      return lighten(theme.palette.background.paper, 0.95);
    case 'default':
    case 'inherit':
      return lighten(theme.palette.background.default, 0.95);
    default:
      if (!theme.palette[color]) {
        console.error(`Invalid color ${color} passed to getLiteColor()!`);
        return lighten(theme.palette.primary.main, 0.95);
      }
      return lighten(theme.palette[color].main, 0.95);
  }
};

type StyleNames = 'colorAndBackground' | 'outline' | 'text';

let useStyles: (props: { color?: ThemeColor }) => Record<StyleNames, string>;
function initStyles() {
  if (useStyles) return useStyles;
  // lazy invoke makeStyles to set this CSS injection order after mui styles
  useStyles = makeStyles((theme: Theme) => ({
    colorAndBackground: ({ color }: { color?: ThemeColor }) => {
      const borderColor = getBorderColor(theme, color);
      return {
        color: getColor(theme, color),
        backgroundColor: getBGColor(theme, color),
        borderColor,
        '&:hover': {
          backgroundColor: getBGColor(theme, color, true),
        },
        '.ioBtnGrpRoot .ioBtnGrpd&:not(:last-of-type)': {
          borderColor: getBorderColor(theme, color),
        },
      };
    },
    text: ({ color }: { color?: ThemeColor }) => ({
      color: getTextColor(theme, color),
      borderColor: getTextColor(theme, color),
      '&:hover': {
        color: getTextColor(theme, color, true),
        backgroundColor: getLiteColor(theme, color),
      },
      '.ioBtnGrpRoot .ioBtnGrpd&:not(:last-of-type)': {
        borderColor: getTextColor(theme, color),
      },
    }),
    outline: ({ color }: { color?: ThemeColor }) => ({
      color: getTextColor(theme, color),
      borderColor: getTextColor(theme, color),
      '&:hover': {
        color: getTextColor(theme, color, true),
        borderColor: getTextColor(theme, color, true),
        backgroundColor: getLiteColor(theme, color),
      },
      '.ioBtnGrpRoot .ioBtnGrpd&:hover': {
        borderColor: getTextColor(theme, color, true),
      },
    }),
  }));
}

export type BGColor = keyof typeof bgColor | string;

export const backgroundColorFn = (theme: Theme, backgroundColor: BGColor) => {
  switch (backgroundColor) {
    case 'transparent':
      return 'transparent';
    case 'default':
      return theme.palette.background.default;
    case 'paper':
      return theme.palette.background.paper;
    default:
      return backgroundColor;
  }
};

export const backgroundColorStyleFn =
  (theme: Theme) =>
  ({ backgroundColor }: { backgroundColor: BGColor }) =>
    backgroundColorFn(theme, backgroundColor);

export const readableColorFn = (theme: Theme, backgroundColor: BGColor) => {
  switch (backgroundColor) {
    case 'transparent':
    case 'default':
    case 'paper':
      return theme.palette.text.primary;
    default: {
      let color = '';
      if (backgroundColor.startsWith('primary')) {
        color = theme.palette.primary.contrastText;
      } else if (backgroundColor.startsWith('secondary')) {
        color = theme.palette.primary.contrastText;
      } else if (
        backgroundColor.startsWith('background') ||
        backgroundColor.startsWith('action') ||
        backgroundColor === 'divider'
      ) {
        color = theme.palette.text.primary;
      } else if (backgroundColor.startsWith('success')) {
        color = theme.palette.primary.contrastText;
      } else if (backgroundColor.startsWith('warning')) {
        color = theme.palette.primary.contrastText;
      } else if (backgroundColor.startsWith('error')) {
        color = theme.palette.primary.contrastText;
      } else {
        const _backgroundColor = (
          styleFunctionSx({
            theme,
            sx: { backgroundColor },
          }) as { backgroundColor: string }
        ).backgroundColor;
        color = tinyColor
          .mostReadable(_backgroundColor, ['#ffffff', '#000000'])
          .toHexString();
        color =
          color === '#000000' && theme.palette.mode === 'light'
            ? theme.palette.text.primary
            : color;
      }

      return color;
    }
  }
};

export const readableColorStyleFn =
  (theme: Theme) =>
  ({ backgroundColor }: { backgroundColor: BGColor }) =>
    readableColorFn(theme, backgroundColor);

const useThemeColor = (color?: ThemeColor): Record<StyleNames, string> => {
  initStyles();
  return useStyles({ color });
};

export { useThemeColor };
